import * as React from 'react';

import { cn } from '../lib/utils';

interface TextareaProps extends React.ComponentProps<'textarea'> {
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoResize = false, ...props }, ref) => {
    const internalRef = React.useRef<HTMLTextAreaElement | null>(null);
    const combinedRef = (instance: HTMLTextAreaElement | null) => {
      if (typeof ref === 'function') {
        ref(instance);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current =
          instance;
      }
      internalRef.current = instance;
    };

    const handleInput = React.useCallback(() => {
      if (autoResize && internalRef.current) {
        internalRef.current.style.height = 'auto';
        internalRef.current.style.height = `${internalRef.current.scrollHeight}px`;
      }
    }, [autoResize]);

    React.useEffect(() => {
      if (autoResize && internalRef.current) {
        handleInput();
      }
    }, [autoResize, props.value, handleInput]);

    return (
      <textarea
        className={cn(
          'vai-flex vai-min-h-[80px] vai-w-full vai-rounded-md vai-border vai-border-input vai-bg-background vai-px-3 vai-py-2 vai-text-base vai-ring-offset-background placeholder:vai-text-muted-foreground focus-visible:vai-outline-none focus-visible:vai-ring-2 focus-visible:vai-ring-ring focus-visible:vai-ring-offset-2 disabled:vai-cursor-not-allowed disabled:vai-opacity-50 md:vai-text-sm',
          className,
        )}
        ref={combinedRef}
        onInput={autoResize ? handleInput : undefined}
        {...props}
      />
    );
  },
);

Textarea.displayName = 'Textarea';

export { Textarea };
